import React, { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Skeleton,
  Chip,
  useTheme,
  Button,
} from "@mui/material";
import {
  useGetSupplierDetailsQuery,
  useAddAndUpdateSupplierMutation,
  useGetSupplierSyncSummaryQuery,
} from "../../../../Services/SupplierService";
import dayjs from "dayjs";
import Breadcrumb from "./../../../../Components/BreadCrumb";
import Grid2 from "@mui/material/Grid";
// import CardMedia from '@mui/material/CardMedia';
import { useConfirmation } from "../../../../Components/ConfirmationDialog";
import { useDispatch } from "react-redux";
import { showSnackbar } from "./../../../../Redux/Slices/NotificationSlice";
import { useDeleteSupplierMutation } from "./../../../../Services/SupplierService";
import SupplierApiKeyComponent from "./SupplierApiKey";
import { API_PROVIDER } from "../../../../Helpers/Constant/CategoryContant";
import SyncHistory from "./SyncHistory";

/**
 * Sync History, released 10 Sep 2026.
 *
 * Runs were recorded by the API throughout the time this was hidden, so the
 * tab opened with 57 real runs behind it rather than an empty table.
 */
/*
 * Whether this supplier syncs at all.
 *
 * Not every supplier does. Content Card and E-Aufladen have their products
 * entered by hand and have never produced a sync run, so a Sync History tab on
 * their page opens onto an empty table and a "Last run: never" card, which
 * reads as something broken rather than something that does not apply.
 *
 * Decided from whether runs exist rather than a hardcoded list of the four that
 * sync today: supplierType says "API Provider" for Content Card as well, so it
 * cannot answer this, and a list would need editing every time a supplier is
 * added. If Content Card is ever put on an API, its first run makes the tab
 * appear on its own.
 */
const supplierHasSyncRuns = (summary: any, supplierId?: string): boolean =>
  !!supplierId &&
  (summary?.data?.perSupplier || []).some((s: any) => s._id === supplierId);
const SupplierCategoryComponent = lazy(() => import("./SupplierCategory"));
const SupplierProducts = lazy(() => import("./SupplierProducts"));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SupplierDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const { requestConfirmation } = useConfirmation();
  const [deleteSupplier] = useDeleteSupplierMutation();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetSupplierDetailsQuery(id || "");
  // Already fetched by the Sync History tab itself; RTK Query serves both from
  // one request.
  const { data: syncSummary } = useGetSupplierSyncSummaryQuery();
  const SHOW_SYNC_HISTORY = supplierHasSyncRuns(
    syncSummary,
    data?.parentSupplierId,
  );
  const [categoryIndex, setCategoryIndex] = useState<number>(0);
  const [logoError, setLogoError] = useState<boolean>(false);
  const [updateCustomer, { isLoading: updateCustomerLoading }] =
    useAddAndUpdateSupplierMutation();

  const breadcrumbItems = [
    // { label: "Dashboard", path: "dashboard" },
    { label: "Suppliers", path: "supplier" },
    { label: "Supplier Details" },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (isLoading) {
    // I want create mui Skeleton like given in main components
    return (
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Breadcrumb items={breadcrumbItems} name={`Supplier`} />
          <Button
            variant="outlined"
            disabled={updateCustomerLoading}
            color="error"
            sx={{ mb: 2 }}
          >
            Loading...
          </Button>
        </Box>
        <Grid2 container spacing={1} sx={{ mt: 2 }}>
          {/* Left Card Skeleton - Customer Profile */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Card elevation={3}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Skeleton variant="circular">
                    <Avatar sx={{ width: 120, height: 120 }} />
                  </Skeleton>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    sx={{ mt: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={80}
                    height={32}
                    sx={{ mt: 1, borderRadius: 16 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  {[
                    "Email",
                    "Phone",
                    "Last Login",
                    "Member Since",
                    "Login Method",
                  ].map((label, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {label}
                      </Typography>
                      <Skeleton variant="text" width="80%" height={24} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          {/* Right Card Skeleton - Tabs */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Card elevation={3}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={0}>
                  {["Supplier Category", "Mapping Category"].map(
                    (label, index) => (
                      <Tab key={index} label={<Skeleton width={80} />} />
                    ),
                  )}
                </Tabs>
              </Box>

              <CardContent>
                {/* Simulating the active tab content */}
                <Grid2 container spacing={2}>
                  {[...Array(3)].map((_, index) => (
                    <Grid2 key={index} size={{ xs: 12, md: 46 }}>
                      <Skeleton
                        variant="rectangular"
                        height={60}
                        sx={{ borderRadius: 1 }}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  const ProductSkeleton = () => {
    return (
      <Grid2 size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            cursor: "pointer",
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{ width: "100%", height: 80, pt: 1 }}
          />
          <CardContent sx={{ textAlign: "center", height: "60px" }}>
            <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} />
            <Skeleton variant="text" width="50%" sx={{ mx: "auto" }} />
          </CardContent>
        </Card>
      </Grid2>
    );
  };

  const handleDelete = () => {
    requestConfirmation({
      title: "Delete Supplier",
      message: "Are you sure you want to delete this supplier?",
      onConfirm: async () => {
        try {
          await deleteSupplier(id || "");
          dispatch(
            showSnackbar({
              message: "Supplier deleted successfully",
              severity: "success",
            }),
          );

          navigate(-1);
        } catch (error) {
          dispatch(
            showSnackbar({
              message: "Failed to delete supplier",
              severity: "error",
            }),
          );
        }
      },
      onCancel: () => {
      },
    });
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumb
          items={breadcrumbItems}
          name={
            data?.supplierType === API_PROVIDER
              ? `Sub Supplier ID #${data?.subSupplierId}`
              : `Supplier ID #${data?.parentSupplierId}`
          }
        />
        {/*
          Opens the one validation-history screen, pre-filtered to this
          account. A tab here used to hold its own copy of that list and
          filtered by the PARENT supplier, so it showed sibling accounts'
          attempts as if they were this one's.
        */}
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate(
              `/supplier/validation-logs?q=${encodeURIComponent(
                data?.subSupplierId || data?.parentSupplierId || "",
              )}`,
            )
          }
        >
          View validation history
        </Button>
        {/* <Button onClick={handleDelete} variant="outlined" disabled={updateCustomerLoading} color="error" sx={{ mb: 2 }}>Delete Supplier</Button> */}
      </Box>
      <Grid2 container spacing={1} sx={{ mt: 2 }}>
        {/* Left Card - Customer Profile */}
        <Grid2 size={{ xs: 12, md: 4, lg: 3 }}>
          <Card elevation={3}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                {data?.logo && !logoError ? (
                  <img
                    src={data?.logo}
                    style={{ width: 120, height: 120 }}
                    alt={data?.supplierName}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <Avatar sx={{ width: 120, height: 120 }}>
                    {data?.supplierName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="h6">{data?.supplierName}</Typography>
                <Chip
                  label={
                    data?.isActive === true
                      ? "Active"
                      : data?.isActive === false
                        ? "Inactive"
                        : "Blocked"
                  }
                  color={
                    data?.isActive === true
                      ? "success"
                      : data?.isActive === false
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Supplier Name
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {data?.name}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {data?.email}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {data?.phone || "N/A"}
                </Typography>

                <Typography variant="subtitle2" color="textSecondary">
                  Member Since
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {dayjs(data?.createdAt).format("DD.MM.YYYY, HH:mm:ss")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        {/*
          Sync History is built but not switched on yet. Flip this to true to
          reveal the tab — nothing else needs changing, and the API records runs
          either way, so history keeps accruing while it is hidden.
        */}
        {/* Right Card - Updated Tabs */}
        <Grid2 size={{ xs: 12, md: 8, lg: 9 }}>
          <Card elevation={3}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Supplier Products" />
                {SHOW_SYNC_HISTORY && <Tab label="Sync History" />}
              </Tabs>
            </Box>

            {/* Supplier Category */}
            {/* <TabPanel value={tabValue} index={0}>
                            {data && data?._id && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <SupplierCategoryComponent supplierInfo={data} />
                                </Suspense>
                            )}
                        </TabPanel> */}

            {SHOW_SYNC_HISTORY && (
              <TabPanel value={tabValue} index={1}>
                <Suspense fallback={<div>Loading...</div>}>
                  <SyncHistory
                    supplierId={data?.parentSupplierId || ""}
                    supplierName={data?.supplierName || ""}
                    subSupplierId={data?.subSupplierId || ""}
                  />
                </Suspense>
              </TabPanel>
            )}

{/*
              The validation history is NOT a tab here.
              It lived on this page and filtered by the PARENT supplier, so
              standing on one account you saw every sibling account's attempts
              with nothing saying so -- invisible only because each parent has
              one account today. Scoped properly it would be near-empty, since
              the attempts worth reading are the refusals, and those happen
              before an account exists to hang them on. The link in the header
              opens the one screen that holds all of them, pre-filtered.
            */}

            <TabPanel value={tabValue} index={0}>
              <Suspense fallback={<div>Loading...</div>}>
                <SupplierProducts
                  supplierId={data?.parentSupplierId || ""}
                  supplierName={data?.supplierName || ""}
                  subSupplierId={data?.subSupplierId || ""}
                  supplierType={data?.supplierType || ""}
                />
              </Suspense>
            </TabPanel>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default SupplierDetails;
